/**
 * withRetry — exponential-backoff retry wrapper for transient RPC/network errors.
 *
 * Safe to use with idempotent RPCs (create_job_from_ai uses ai_action_dedup to
 * deduplicate on the server side when p_idempotency_key is provided).
 *
 * Retryable status codes: none (pure network failure), 408, 429, 500, 502, 503, 504.
 * Non-retryable: 400, 401, 403, 404, 409, AI enforcement codes, or any error
 * where shouldRetry returns false.
 *
 * Back-off: baseDelayMs * 2^(attempt - 1)  →  600 ms, 1200 ms (for 3 attempts).
 */

const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);
const AI_ENFORCEMENT_CODES = new Set([
  'AI_NOT_ENABLED',
  'AI_LICENSE_BLOCKED',
  'AI_USAGE_CAP_REACHED',
  // create_job_from_ai raises these SQLSTATEs (message = the code above);
  // PostgREST puts the SQLSTATE in `code` (RD-PLAT-014).
  'CVA01',
  'CVA02',
]);

/**
 * Default predicate: retry only transient failures.
 * Business-logic / auth errors from the RPC carry a numeric status (or an AI
 * enforcement code) and must not be retried.
 *
 * @param {Error} err
 * @returns {boolean}
 */
function defaultShouldRetry(err) {
  if (typeof err?.code === 'string' && AI_ENFORCEMENT_CODES.has(err.code)) {
    return false;
  }
  const status = typeof err?.status === 'number' ? err.status : null;
  if (status === null) {
    // No HTTP status → pure network failure / fetch abort → retryable
    return true;
  }
  return RETRYABLE_STATUSES.has(status);
}

/**
 * Calls fn() up to maxAttempts times with exponential back-off.
 * Rethrows the last error when all attempts are exhausted or the error is
 * non-retryable.
 *
 * @template T
 * @param {() => Promise<T>} fn            The async operation to retry.
 * @param {object}           [options]
 * @param {number}           [options.maxAttempts=3]      Total attempts.
 * @param {number}           [options.baseDelayMs=600]    First back-off in ms.
 * @param {(err: Error) => boolean} [options.shouldRetry] Override retry predicate.
 * @returns {Promise<T>}
 */
export async function withRetry(fn, options = {}) {
  const {
    maxAttempts = 3,
    baseDelayMs = 600,
    shouldRetry = defaultShouldRetry,
  } = options;

  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn(); // eslint-disable-line no-await-in-loop
    } catch (err) {
      lastError = err;
      const isLast = attempt === maxAttempts;
      if (isLast || !shouldRetry(err)) {
        throw err;
      }
      const delay = baseDelayMs * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay)); // eslint-disable-line no-await-in-loop
    }
  }
  // Unreachable; satisfies linters.
  throw lastError;
}
